import { useTranslation } from "react-i18next";
import { Archive, Shield, ShieldCheck, UserCheck } from "lucide-react";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";

interface ArchivedBadgeProps {
  /** "outline" — dashed border, mono text (table rows). "solid" — filled pill (drawer headers). */
  variant?: "outline" | "solid";
}

/**
 * "Archived" pill shown next to role/org names. Single source for the
 * near-identical badges previously duplicated in RolesTable, RoleDrawer,
 * and OrgDrawer.
 */
export function ArchivedBadge({ variant = "outline" }: ArchivedBadgeProps) {
  const { t } = useTranslation();
  const label = t("iam.shared.archivedBadge", "Archived");

  if (variant === "solid") {
    return (
      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
        <Archive className="h-2.5 w-2.5" />
        {label}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 px-1.5 py-px font-mono text-[10px] text-muted-foreground">
      <Archive className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

export type RoleType = "system" | "custom";

function useRoleTypeCopy(type: RoleType) {
  const { t } = useTranslation();
  const label = type === "system"
    ? t("iam.rolesTable.systemRoleTooltip", "Built-in system role")
    : t("iam.rolesTable.customRoleTooltip", "Custom role");
  const description = type === "system"
    ? t(
        "iam.rolesTable.systemRoleDescription",
        "Built-in role managed by Nexus. Its permissions are predefined — it can't be edited or deleted.",
      )
    : t(
        "iam.rolesTable.customRoleDescription",
        "Role created by your team for this organization. You can edit its permissions, rename, or delete it.",
      );
  return { label, description };
}

/** Mono "system"/"custom" tag with an explanatory tooltip. */
export function RoleTypeBadge({ type }: { type: RoleType }) {
  const { label, description } = useRoleTypeCopy(type);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={label}
          className={cn(
            "inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[11px] font-medium",
            type === "system"
              ? "bg-muted text-muted-foreground border-border"
              : "bg-primary/10 text-primary border-primary-border",
          )}
        >
          {type}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">
        <span className="font-medium">{label}</span>
        <span className="mt-0.5 block text-tooltip-foreground/80">{description}</span>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * "Your role" pill shown next to a role the current user belongs to. Ties into
 * the self-edit block on `PUT /access/permissions` — the tooltip explains why
 * "Edit permissions" is disabled for it elsewhere in the row/drawer.
 */
export function YourRoleBadge() {
  const { t } = useTranslation();
  const label = t("iam.shared.yourRoleBadge", "Your role");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={label}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary-border bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
        >
          <UserCheck className="h-2.5 w-2.5" />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px]">
        {t(
          "iam.shared.yourRoleBadgeTooltip",
          "You're a member of this role. You can't edit your own permissions — ask a platform admin.",
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/** Shield glyph (system = filled check, custom = outline) with the same tooltip copy as `RoleTypeBadge`. */
export function RoleTypeIcon({ type }: { type: RoleType }) {
  const { label } = useRoleTypeCopy(type);
  const Icon = type === "system" ? ShieldCheck : Shield;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label={label} className="inline-flex shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
